import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Rocket, Package, Truck, Wallet, TrendingUp, Settings,
  Play, FileText, CheckSquare, Clock, CheckCircle2, Circle,
  ChevronRight, BookOpen, Award, Target, ArrowLeft, X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icons from backend
const iconMap = {
  Rocket, Package, Truck, Wallet, TrendingUp, Settings,
  Play, FileText, CheckSquare, BookOpen, Award, Target
};

const TrainingAcademy = () => {
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moduleProgress, setModuleProgress] = useState({});

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch sections (include admin content if admin)
      const sectionsRes = await axios.get(`${API_URL}/api/training/sections`, {
        params: { admin_view: isAdmin }
      });
      setSections(sectionsRes.data);

      // Fetch progress for vendor
      if (user?.id) {
        const progressRes = await axios.get(`${API_URL}/api/training/progress/${user.id}`);
        setProgress(progressRes.data);
        
        // Create a map of section progress
        const progressMap = {};
        progressRes.data.section_progress?.forEach(sp => {
          progressMap[sp.section_id] = sp;
        });
        setModuleProgress(progressMap);
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleProgress = async (moduleId) => {
    if (!user?.id) return null;
    try {
      const res = await axios.get(`${API_URL}/api/training/progress/${user.id}/module/${moduleId}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching module progress:', error);
      return null;
    }
  };

  const markModuleComplete = async (moduleId) => {
    if (!user?.id) return;
    try {
      await axios.post(`${API_URL}/api/training/progress/${user.id}/complete/${moduleId}`);
      // Refresh data
      fetchData();
      // Refresh module if currently selected
      if (selectedModule?.id === moduleId) {
        const updatedProgress = await fetchModuleProgress(moduleId);
        setSelectedModule(prev => ({ ...prev, userProgress: updatedProgress }));
      }
    } catch (error) {
      console.error('Error marking module complete:', error);
    }
  };

  const updateModuleProgress = async (moduleId, status, checklistCompleted = []) => {
    if (!user?.id) return;
    try {
      await axios.post(`${API_URL}/api/training/progress/${user.id}`, {
        module_id: moduleId,
        status,
        checklist_completed: checklistCompleted
      });
      fetchData();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setSelectedModule(null);
  };

  const handleModuleClick = async (module) => {
    const userProgress = await fetchModuleProgress(module.id);
    setSelectedModule({ ...module, userProgress });
    
    // Auto-mark as in_progress if trackable and not started
    if (module.is_trackable && (!userProgress || userProgress.status === 'not_started')) {
      updateModuleProgress(module.id, 'in_progress');
    }
  };

  const handleBackToModules = () => {
    setSelectedModule(null);
  };

  const getSectionIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || BookOpen;
    return IconComponent;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <Circle className="h-5 w-5 text-gray-300" />;
    }
  };

  const renderModuleContent = () => {
    if (!selectedModule) return null;

    const { content_type, content, video_url, video_source_type, video_media_id, checklist_items, userProgress } = selectedModule;
    
    // Determine video URL based on source type
    const getVideoSrc = () => {
      if (video_source_type === 'youtube' && video_url) {
        return video_url;
      }
      if (video_source_type === 'media' && video_media_id) {
        return `${API_URL}/api/files/${video_media_id}`;
      }
      return video_url; // fallback for legacy data
    };
    
    const videoSrc = getVideoSrc();
    const hasVideo = video_source_type && video_source_type !== 'none' && (video_url || video_media_id);

    return (
      <div className="space-y-6">
        {/* Video Section - Always at top for all content types */}
        {hasVideo ? (
          <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
            {video_source_type === 'youtube' ? (
              <iframe
                src={videoSrc}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedModule.title}
              />
            ) : (
              <video
                src={videoSrc}
                className="w-full h-full"
                controls
                poster={selectedModule.video_thumbnail}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        ) : (
          /* Video Placeholder - Show for all modules without video */
          <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="h-10 w-10 text-gray-500 ml-1" />
              </div>
              <p className="text-gray-500 font-medium">Video Coming Soon</p>
              <p className="text-gray-400 text-sm mt-1">Check back later for video content</p>
            </div>
          </div>
        )}

        {/* Text Content Section - Below video */}
        <div className="border-t pt-6">
          {/* Article Content */}
          {content && (
            <div 
              className="prose prose-sm max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}

          {/* Checklist Content */}
          {content_type === 'checklist' && checklist_items?.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Complete this checklist:</h4>
              {checklist_items.map((item, index) => {
                const isCompleted = userProgress?.checklist_completed?.includes(index);
                return (
                  <div
                    key={item.id || item.name || `${item.title || "item"}-${index}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      if (!selectedModule.is_trackable) return;
                      const currentCompleted = userProgress?.checklist_completed || [];
                      const newCompleted = isCompleted
                        ? currentCompleted.filter(i => i !== index)
                        : [...currentCompleted, index];
                      updateModuleProgress(selectedModule.id, 'in_progress', newCompleted);
                    }}
                  >
                  {isCompleted ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 flex-shrink-0" />
                  )}
                  <span className={isCompleted ? 'text-green-700' : 'text-gray-700'}>{item}</span>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Mark Complete Button */}
        {selectedModule.is_trackable && userProgress?.status !== 'completed' && (
          <div className="pt-4 border-t">
            <Button 
              onClick={() => markModuleComplete(selectedModule.id)}
              className="w-full"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Mark as Complete
            </Button>
          </div>
        )}

        {/* Completed Badge */}
        {userProgress?.status === 'completed' && (
          <div className="pt-4 border-t">
            <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 py-3 rounded-lg">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Completed</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render module list
  const renderModuleList = () => {
    if (!selectedSection?.modules?.length) {
      return (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No modules available in this section yet</p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-[450px]">
        <div className="space-y-2 pr-4">
          {selectedSection.modules.map((module, index) => (
            <div
              key={module.id}
              onClick={() => handleModuleClick(module)}
              className="p-4 rounded-lg cursor-pointer transition-all border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            >
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 font-medium text-sm flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{module.title}</h4>
                    {module.admin_only && (
                      <Badge variant="outline" className="text-xs">Admin</Badge>
                    )}
                    {module.is_trackable && (
                      <Badge variant="secondary" className="text-xs">Tracked</Badge>
                    )}
                  </div>
                  {module.description && (
                    <p className="text-sm text-gray-500 line-clamp-2">{module.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      {module.content_type === 'video' && <Play className="h-3 w-3" />}
                      {module.content_type === 'article' && <FileText className="h-3 w-3" />}
                      {module.content_type === 'checklist' && <CheckSquare className="h-3 w-3" />}
                      {module.content_type}
                    </span>
                    {module.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {module.duration_minutes} min
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {module.is_trackable && getStatusIcon(module.userProgress?.status)}
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Academy</h1>
            <p className="text-gray-500">Learn how to maximize your success on the platform</p>
          </div>
          {progress && (
            <Card className="px-4 py-3">
              <div className="flex items-center gap-4">
                <Award className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-500">Overall Progress</p>
                  <div className="flex items-center gap-2">
                    <Progress value={progress.overall_percentage} className="w-24 h-2" />
                    <span className="font-bold text-lg">{progress.overall_percentage}%</span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Sections List */}
          <div className="col-span-12 lg:col-span-4">
            <Card>
              <CardHeader>
                <CardTitle>Training Sections</CardTitle>
                <CardDescription>Select a section to view its modules</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-4 space-y-2">
                    {sections.map((section) => {
                      const IconComponent = getSectionIcon(section.icon);
                      const sectionProgress = moduleProgress[section.id];
                      const isSelected = selectedSection?.id === section.id;

                      return (
                        <div
                          key={section.id}
                          onClick={() => handleSectionClick(section)}
                          className={`p-4 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                isSelected ? 'bg-white/10' : ''
                              }`}
                              style={{ backgroundColor: isSelected ? undefined : `${section.color}20` }}
                            >
                              <IconComponent
                                className="h-5 w-5"
                                style={{ color: isSelected ? 'white' : section.color }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className={`font-medium truncate ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                  {section.title}
                                </h3>
                                {section.admin_only && (
                                  <Badge variant="outline" className="text-xs">Admin</Badge>
                                )}
                              </div>
                              <p className={`text-sm truncate ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                                {section.total_modules} modules
                              </p>
                              {sectionProgress && section.trackable_modules > 0 && (
                                <div className="mt-2">
                                  <Progress
                                    value={sectionProgress.percentage}
                                    className={`h-1 ${isSelected ? 'bg-white/20' : ''}`}
                                  />
                                </div>
                              )}
                            </div>
                            <ChevronRight className={`h-5 w-5 flex-shrink-0 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Module List OR Content View */}
          <div className="col-span-12 lg:col-span-8">
            {!selectedSection ? (
              // No section selected
              <Card className="h-[500px] flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">Select a Section</h3>
                  <p className="text-gray-500">Choose a training section from the left to view its modules</p>
                </div>
              </Card>
            ) : selectedModule ? (
              // Module content view
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleBackToModules}
                        className="mr-2"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </Button>
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {selectedModule.title}
                          {selectedModule.is_trackable && (
                            <Badge variant="secondary" className="text-xs">Tracked</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{selectedModule.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedModule.duration_minutes && (
                        <Badge variant="outline">
                          <Clock className="h-3 w-3 mr-1" />
                          {selectedModule.duration_minutes} min
                        </Badge>
                      )}
                      <Badge variant="outline">
                        {selectedModule.content_type === 'video' && <Play className="h-3 w-3 mr-1" />}
                        {selectedModule.content_type === 'article' && <FileText className="h-3 w-3 mr-1" />}
                        {selectedModule.content_type === 'checklist' && <CheckSquare className="h-3 w-3 mr-1" />}
                        {selectedModule.content_type}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    {renderModuleContent()}
                  </ScrollArea>
                </CardContent>
              </Card>
            ) : (
              // Module list view
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const IconComponent = getSectionIcon(selectedSection.icon);
                      return (
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${selectedSection.color}20` }}
                        >
                          <IconComponent className="h-6 w-6" style={{ color: selectedSection.color }} />
                        </div>
                      );
                    })()}
                    <div>
                      <CardTitle>{selectedSection.title}</CardTitle>
                      <CardDescription>{selectedSection.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {renderModuleList()}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainingAcademy;
